import ReactDOM from 'react-dom';
import { Suspense, lazy } from 'react';
import { ToastContainer, cssTransition } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Route, Router, Switch } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import * as serviceWorker from './serviceWorker';
import './index.scss';
import history from './helpers/history';
import ProtectedRoute from './components/ProtectedRoute';
import { PageProvider } from './layout/pageContext';
import { SidebarProvider } from './layout/sidebarContext';
import { QueryContextProvider } from './pages/workorders/wo-context';
import HomeRoute from './components/HomeRoute';
import { getUserData } from './_actions';
import InventoryManagenemtReports from './pages/reports/IventoryManagementReports';
import FormsMapperListPage from './pages/mappingTool/index';
import UploadForm from './pages/mappingTool/UploadForm';
import MappingTool from './pages/mappingTool/MappingTool';
import ReviewPdf from './pages/mappingTool/ReviewPdf';
import TestingMappedPDF from './pages/mappingTool/TestingMappedPDF';

const retryLazy = (lazyExecution) => {
  // this is helper func to rerun the failed chunk load when user has old build
  return new Promise((resolve, reject) => {
    function rerun() {
      lazyExecution()
        .then(resolve)
        .catch((err) => {
          if (err.name === 'ChunkLoadError') {
            // reload to get new build
            window.location.reload();
          } else {
            reject(err);
          }
        });
    }
    rerun();
  });
};

const Login = lazy(() => retryLazy(() => import('./pages/authentication/login')));
const Logout = lazy(() => retryLazy(() => import('./pages/authentication/logout')));
const ForgotPassword = lazy(() => retryLazy(() => import('./pages/authentication/forgotPassword')));
const ResetPassword = lazy(() => retryLazy(() => import('./pages/authentication/ResetPassword')));
const Register = lazy(() => retryLazy(() => import('./pages/authentication/registration')));
const Dashboard = lazy(() => retryLazy(() => import('./pages/dashboard')));
const Notifications = lazy(() => retryLazy(() => import('./pages/notifications')));
const UpdateCaches = lazy(() => retryLazy(() => import('./pages/user/UpdateCaches')));
const IssueDetail = lazy(() => retryLazy(() => import('./pages/issues')));
const GroupDetail = lazy(() => retryLazy(() => import('./pages/issues/group')));
const Analytics = lazy(() => retryLazy(() => import('./pages/analytics')));
const SharedFilesHOC = lazy(() => retryLazy(() => import('./pages/sharedFiles')));
const LineItems = lazy(() => retryLazy(() => import('./pages/lineitems')));
const TestTracking = lazy(() => retryLazy(() => import('./pages/testtracking')));
const WorkOrders = lazy(() => retryLazy(() => import('./pages/workorders')));
const AssignedDeficiencies = lazy(() =>
  retryLazy(() => import('./pages/workorders/AssignedDeficiencies'))
);
const SingleWorkOrder = lazy(() => retryLazy(() => import('./pages/singleWorkorder')));
const PageNotFound = lazy(() => retryLazy(() => import('./pages/base/404')));
const ShowImage = lazy(() => retryLazy(() => import('./pages/base/ShowImage')));
const ShowXml = lazy(() => retryLazy(() => import('./pages/base/ShowXml')));
const PageNotFoundUnauthorise = lazy(() => retryLazy(() => import('./pages/base/404')));
const AllReports = lazy(() => retryLazy(() => import('./pages/reports/All')));
const RecapReport = lazy(() => retryLazy(() => import('./pages/reports/Recap')));
const PeriodicOverviewReport = lazy(() => retryLazy(() => import('./pages/reports/PeriodicOverviewReport')));
const InventoryManagementReports = lazy(() => retryLazy(() => import("./pages/reports/IventoryManagementReports")));
const RegulatorReport = lazy(() => retryLazy(() => import('./pages/reports/Regulator')));
const RemedyReport = lazy(() => retryLazy(() => import('./pages/reports/Remedy')));
const AnalyticsReport = lazy(() => retryLazy(() => import('./pages/reports/Analytics')));
const AnalyticsReportV2 = lazy(() => retryLazy(() => import('./pages/reports/Analytics')));
// const FacilityProfile = lazy(() => import('./pages/facilityProfile'));
const CathodicProtectionReport = lazy(() => retryLazy(() => import('./pages/reports/CathodicProtection')));
const HotTopicReport = lazy(() => retryLazy(() => import('./pages/reports/HotTopic')));
const WidgetsReport = lazy(() => retryLazy(() => import('./pages/reports/Widgets')));
const TermsOfService = lazy(() => retryLazy(() => import('./pages/terms')));
const Inspections = lazy(() => retryLazy(() => import('./pages/inspections')));
const RegulatoryInspections = lazy(() => retryLazy(() => import('./pages/regulatoryInspections')));
const ShowPdf = lazy(() => retryLazy(() => import('./pages/base/ShowPdf')));
const DeficienciesAnalytics = lazy(() => retryLazy(() => import('./pages/deficienciesAnalytics')));
const DownloadPDF = lazy(() => retryLazy(() => import('./pages/base/DownloadPDF')));
const ATGMonitoring = lazy(() => retryLazy(() => import('./pages/atgMonitoring')));
const ReleaseDetection = lazy(() => retryLazy(() => import('./pages/releaseDetection')));
const ATGSetupCenter = lazy(() => retryLazy(() => import('./pages/setupCenter')));
const ScoreCard = lazy(() => retryLazy(() => import('./pages/scoreCard')));
const DailyOverviewReport = lazy(() => retryLazy(() => import('./pages/reports/DailyOverviewReport')));
const ATGConnectionCenter = lazy(() => retryLazy(() => import('./pages/atgConnectionCenter')));
const ScoreCardReport = lazy(() => retryLazy(() => import('./pages/reports/ScoreCardReport')));
const PrivacyPolicy = lazy(() => retryLazy(() => import('./pages/policy')));
const AcquisitionReport = lazy(() => retryLazy(() => import('./pages/reports/AcquisitionReport')));
const InspectionAuditReport = lazy(() => retryLazy(() => import('./pages/reports/InspectionAuditReport')));
const FacilityComplianceReport = lazy(() => retryLazy(() => import('./pages/reports/FacilityComplianceReport')));
const OWSWaterLevelReport = lazy(() => retryLazy(() => import('./pages/reports/OWSWaterLevelReport')));
const DetailsPage = lazy(() => retryLazy(() => import('./pages/testtracking/DetailsPage')));
const CabinetV3 = lazy(() => retryLazy(() => import('./pages/cabinetV3')));
const Learning = lazy(() => retryLazy(() => import('./pages/learning')));
const AlarmsV2 = lazy(() => retryLazy(() => import('./pages/atgAlarmsV2')));
const PhotoResearch = lazy(() => retryLazy(() => import('./pages/PhotoResearch')));
const Calendar = lazy(() => retryLazy(() => import('./pages/calendar')));
const Vendors = lazy(() => retryLazy(() => import('./pages/vendors')));
const Map = lazy(() => retryLazy(() => import('./pages/map')));
// const RoutingGantt = lazy(() => retryLazy(()=> import('./pages/routingGantt')));
// const GanttPage = lazy(() => retryLazy(()=> import('./pages/ganttPage')));
const Inventory = lazy(() => retryLazy(() => import('./pages/inventory')));
const FacilityProfileNew = lazy(() => retryLazy(() => import('./pages/facilityProfile2')));
const MonthlyComparisonReport = lazy(() => retryLazy(() => import('./pages/reports/MonthlyComparisonReport')));
const ATGTerminal = lazy(() => retryLazy(() => import('./pages/atgTerminal')));
const InvoiceApproval = lazy(() => retryLazy(() => import('./pages/invoiceApproval')));
const JobMonitoring = lazy(() => retryLazy(() => import('./pages/jobs_monitoring')));
const FacilityList = lazy(() => retryLazy(() => import('./pages/facilityList')));
const InventoryVariance = lazy(() => retryLazy(() => import('./pages/inventoryVariance')));
const AddRegulatoryEvent = lazy(() => retryLazy(() => import('./pages/facilityProfile2/AddRegulatoryEvents')));
const RegulatoryDetails = lazy(() => retryLazy(() => import('./pages/regulatoryInspections/RegulatoryDetails')));
const Permits = lazy(() => retryLazy(() => import('./pages/permits')));
const PermitDetails = lazy(() => retryLazy(() => import('./pages/permits/PermitDetails')));


if (!window.origin.includes('local')) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false
      })
    ],
    environment: process.env.REACT_APP_SENTRY_ENV || 'NoEnv',
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: ['localhost', /^https:\/\/proteanapp\.com\/api/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
  const dataUser = getUserData();
  Sentry.setContext('ExtraData', {
    profile: dataUser?.current_user || 'NoUser',
    roles: dataUser?.roles || 'NoRoles'
  });
}

function at(n) {
  // ToInteger() abstract op
  n = Math.trunc(n) || 0;
  // Allow negative indexing from the end
  if (n < 0) n += this.length;
  // OOB access is guaranteed to return undefined
  if (n < 0 || n >= this.length) return undefined;
  // Otherwise, this is just normal property access
  return this[n];
}

if (!Event.prototype.hasOwnProperty('path')) {
  Object.defineProperty(Event.prototype, 'path', {
    get() {
      return this.composedPath();
    }
  });
}

const TypedArray = Reflect.getPrototypeOf(Int8Array);
for (const C of [Array, String, TypedArray]) {
  Object.defineProperty(C.prototype, 'at', {
    value: at,
    writable: true,
    enumerable: false,
    configurable: true
  });
}




const App = () => {
  return (
    <Router history={history}>
      {/* <Suspense fallback={<Page documentName='Loading' path='Loading' pageName="loading"><div /></Page>}> */}
      <Suspense fallback={<div />}>
        <Switch>
          <Route exact path="/">
            {/* <Redirect to={{ pathname: '/dashboard' }} /> */}
            <HomeRoute />
          </Route>
          <ProtectedRoute
            exact
            visible="unauthorized"
            path="/login"
            component={Login}
            name="login"
          />
          <ProtectedRoute
            exact
            visible="unauthorized"
            path="/forgot-password"
            component={ForgotPassword}
            name="forgot_password"
          />
          <ProtectedRoute
            exact
            visible="unauthorized"
            path="/reset-password/:token"
            component={ResetPassword}
            name="reset_password"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/logout"
            component={Logout}
            name="logout"
          />
          <ProtectedRoute
            exact
            visible="unauthorized"
            path="/self-registration/:token"
            component={Register}
            name="register"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/dashboard"
            component={Dashboard}
            name="dashboard"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/notifications"
            component={Notifications}
            name="notifications"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/calendar"
            component={Calendar}
            name="dashboard"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/facility_profile/add_regulatory_event"
            component={AddRegulatoryEvent}
            name="add_regulatory_event"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/facility_profile/regulatory_action_tracking_details"
            component={RegulatoryDetails}
            name="regulatory_action_tracking_details"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/workorders"
            component={WorkOrders}
            name="workorders_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/assigned_deficiencies"
            component={AssignedDeficiencies}
            name="workorders_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/workorders/:id"
            component={SingleWorkOrder}
            name="workorders_enabled"
            detail
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/lineitems"
            component={LineItems}
            name="workorders_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/invoice_approval"
            component={InvoiceApproval}
            name="invoice_approval_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/issues/:id"
            component={IssueDetail}
            name="issue_tracker_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/groups/:id"
            component={GroupDetail}
            name="issue_tracker_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/e-cabinet"
            component={CabinetV3}
            name="file_cabinet_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/e-cabinet/id/:fileId"
            component={CabinetV3}
            name="file_cabinet_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/e-cabinet/search/:query"
            component={CabinetV3}
            name="file_cabinet_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/inspections"
            component={Inspections}
            name="analytics_enabled"
          />
          <ProtectedRoute
            exact
            // visible="protected"
            path="/regulatory"
            component={RegulatoryInspections}
            name="regulatory_inspections"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/analytics"
            component={Analytics}
            name="analytics_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/tests"
            component={TestTracking}
            name="test_tracking_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/tests/:testId"
            component={DetailsPage}
            name="test_tracking_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/revaspitas"
            component={TestTracking}
            name="test_tracking_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/revaspitas/:testId"
            component={DetailsPage}
            name="test_tracking_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/reports/all"
            component={AllReports}
            name="reports_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/reports/analytics_report"
            component={AnalyticsReport}
            name="reports_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/reports/inventory_management_reports"
            component={InventoryManagenemtReports}
            feature={"inventory_management.reports"}
            name="inventory_management_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/dev/reports/analytics_report"
            component={AnalyticsReportV2}
            name="reports_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/reports/daily_overview"
            component={DailyOverviewReport}
            name="daily_overview_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/reports/periodic_overview"
            component={PeriodicOverviewReport}
            name="reports_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/reports/recap"
            component={RecapReport}
            name="reports_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/reports/remedy"
            component={RemedyReport}
            name="reports_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/reports/regulator"
            component={RegulatorReport}
            name="reports_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/reports/hot_topic"
            component={HotTopicReport}
            name="reports_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/reports/cathodic_protection"
            component={CathodicProtectionReport}
            name="reports_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/reports/widgets"
            component={WidgetsReport}
            name="reports_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/reports/monthly_comparison_report"
            component={MonthlyComparisonReport}
            name="reports_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/reports/acquisition"
            component={AcquisitionReport}
            name="reports_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/reports/inspection_audit"
            component={InspectionAuditReport}
            name="reports_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/reports/facility_compliance"
            component={FacilityComplianceReport}
            name="reports_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/reports/ows_water_level"
            component={OWSWaterLevelReport}
            name="reports_enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/facility_profile/:id"
            component={FacilityProfileNew}
            name="facility_profile"
          />

          <ProtectedRoute
            exact
            visible="protected"
            path="/facility_list"
            component={FacilityList}
            name="facility_profile"
          />

          <ProtectedRoute
            exact
            visible="protected"
            path="/permit_details/:siteId/:permitId"
            component={PermitDetails}
            name="permit_details"
          />

          <ProtectedRoute
            exact
            visible="protected"
            path="/remote_monitoring"
            component={ATGMonitoring}
            name="atg"
            feature="atg.enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/release_detection"
            component={ReleaseDetection}
            name="release_detection_enabled"
            feature="release_detection.access"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/setup_center"
            component={ATGSetupCenter}
            name="atg"
            feature="atg.setup_center.access"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/alarms"
            component={AlarmsV2}
            name="atg"
            feature="atg.enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/connection_center"
            component={ATGConnectionCenter}
            name="atg"
            feature="atg.enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/atg_terminal"
            component={ATGTerminal}
            name="atg"
            feature="atg.enabled"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/inventory_management"
            component={Inventory}
            name="inventory_management_enabled"
            feature="inventory_management.access"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/image/:imageId"
            component={ShowImage}
            name="show_image"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/xml/:fileId"
            component={ShowXml}
            name="show_xml"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/pdf/:fileId"
            component={ShowPdf}
            name="show_pdf"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/download_report/:reportId/"
            component={DownloadPDF}
            name="show_pdf"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/performance"
            component={ScoreCard}
            name="show_pdf"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/deficiency"
            component={ScoreCard}
            name="show_pdf"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/deficiency_specifics"
            component={ScoreCard}
            name="show_pdf"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/scorecard_report"
            component={ScoreCardReport}
            name="show_pdf"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/max"
            component={DeficienciesAnalytics}
            name="quick_dispatch_enabled"
            feature={'quick_dispatch.read'}
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/vendors"
            component={Vendors}
            name="quick_dispatch_enabled"
            feature="vendor.read"
          />
          {/* <ProtectedRoute
            exact
            visible="protected"
            path="/routing"
            component={RoutingGantt}
            name="show_pdf"
          /> */}
          {/* <ProtectedRoute
            exact
            visible="protected"
            path="/routing2"
            component={GanttPage}
            name="show_pdf"
          /> */}
          <ProtectedRoute
            exact
            visible="protected"
            path="/404"
            component={PageNotFound}
            name="404"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/photoResearch"
            component={PhotoResearch}
            name="photoResearch"
          />
          <ProtectedRoute exact visible="protected" path="/map" component={Map} name="show_pdf" />
          <ProtectedRoute
            exact
            visible="protected"
            path="/update_caches"
            component={UpdateCaches}
            name="system_tools"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/permits"
            component={Permits}
            name="permits"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/jobs"
            component={JobMonitoring}
            name="system_tools"
          />
          <Route
            exact
            path="/e-cabinet/share/:fileId/:subId"
            component={SharedFilesHOC}
            name="share"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/report_template_mapper/list"
            component={FormsMapperListPage}
            name="forms_mapper"
            feature="mapping_tool.read"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/report_template_mapper/upload"
            component={UploadForm}
            name="forms_mapper"
            feature="mapping_tool.write"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/report_template_mapper/tool"
            component={MappingTool}
            name="forms_mapper"
            feature="mapping_tool.write"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/report_template_mapper/review"
            component={ReviewPdf}
            name="forms_mapper"
            feature="mapping_tool.write"
          />
          <ProtectedRoute
            exact
            visible="protected"
            path="/report_template_mapper/testing"
            component={TestingMappedPDF}
            name="forms_mapper"
            feature="mapping_tool.write"
          />
          <Route exact path="/e-cabinet/share/:fileId" component={SharedFilesHOC} name="share" />
          <Route exact path="/learning" component={Learning} name="share" />
          <Route exact path="/terms_of_service" component={TermsOfService} name="terms_service" />
          <Route exact path="/privacy_policy" component={PrivacyPolicy} name="terms_service" />
          <Route exact path="/inventory_variance/:tabName" component={InventoryVariance} name="inventory_variance" />
          <Route component={PageNotFoundUnauthorise} />
        </Switch>
      </Suspense>
    </Router>
  );
};

const Zoom = cssTransition({
  enter: 'zoomIn',
  exit: 'zoomOut'
});

ReactDOM.render(
  <PageProvider>
    <SidebarProvider>
      <QueryContextProvider>
        <ToastContainer
          className="notification-toast"
          position="top-right"
          autoClose={5000}
          hideProgressBar
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          transition={Zoom}
        />
        <App />
      </QueryContextProvider>
    </SidebarProvider>
  </PageProvider>,
  document.getElementById('root')
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
